import { Component, Inject, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { AppService, GRSSResponse } from 'src/app/_services/app.service';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { trigger, state, style, transition, animate } from '@angular/animations';

interface Redirect {
  url: string;
  marketId?: string;
}

interface SidebarMenu {
  menuId?: string;
  name: string;
  icon?: string;
  roles?: SidebarMenuRoles[];
  childMenus?: SidebarMenu[];
  redirect?: Redirect;
  url?: string;
  order?: string;
  hovered?: boolean;
  level?: number;
  expandable?: boolean;
}

interface SidebarMenuRoles {
  roleId: string;
  name: string;
  enabled: boolean;
  default: boolean;
}


@Component({
  selector: 'app-manage-menus',
  standalone: false,
  templateUrl: './manage-menus.component.html',
  styleUrls: ['./manage-menus.component.css'],
  animations: [
    trigger('slideVertical', [
      state('*', style({ height: 0 })),
      state('show', style({ height: '*' })),
      transition('* => *', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')])
    ])
  ]
})
export class ManageMenusComponent implements OnInit {

  displayColumns: string[] = ['role', 'enabled', 'default'];
  selectedSidebarMenu: SidebarMenu;

  hoveredNode: SidebarMenu | null = null;
  isHoveringTrigger = false;
  isHoveringDropdown = false;

  private _transformer = (node: SidebarMenu, level: number): SidebarMenu => ({
    ...node,
    level,
    expandable: !!node.childMenus && node.childMenus.length > 0,
    hovered: false
  });

  treeControl = new FlatTreeControl<SidebarMenu>(
    node => node.level,
    node => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    node => node.level,
    node => node.expandable,
    node => node.childMenus
  );

  dataSourceTree = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  constructor(
    @Inject('BASE_URL') private baseUrl: string,
    private http: HttpClient,
    public service: AppService,
    private translate: TranslateService
  ) {
    this.service.SetTranslation('administration/manage-menus');
  }

  ngOnInit() {
    this.getMenus();
  }

  getMenus() {
    let currentLanguage = this.translate.getBrowserLang();
    if (!this.service.supportedLanguages.includes(currentLanguage)) currentLanguage = 'en';

    this.http.get<SidebarMenu[]>(this.baseUrl + 'api/menus/getMenus/' + currentLanguage).subscribe(result => {
      result.forEach(menu => (menu.hovered = false));
      this.dataSourceTree.data = result;
      if (result.length > 0) {
        this.selectedSidebarMenu = result[0];
      }
    });
  }

  hasChild = (_: number, node: SidebarMenu) => node.expandable;

  onClickNode(node: SidebarMenu) {
    this.selectedSidebarMenu = node;
  }

  onMouseEnterTrigger(node: SidebarMenu) {
    this.hoveredNode = node;
    this.isHoveringTrigger = true;
  }

  onMouseLeaveTrigger() {
    this.isHoveringTrigger = false;
    this.checkHoverState();
  }

  onMouseEnterDropdown() {
    this.isHoveringDropdown = true;
  }

  onMouseLeaveDropdown() {
    this.isHoveringDropdown = false;
    this.checkHoverState();
  }

  private checkHoverState() {
    if (!this.isHoveringTrigger && !this.isHoveringDropdown) {
      this.hoveredNode = null;
    }
  }

  onSaveChanges() {
    this.service.showSpinner();
    this.http.post<GRSSResponse>(this.baseUrl + 'api/menus/updateChanges', this.dataSourceTree.data).subscribe(result => {
      const success = result.statusCode === 4;
      this.service.SaveStatus(success, 'Menus.Messages.msgHeaderUpdateMenu', result.statusMessage);
    }).add(() => {
      this.service.hideSpinner();
    });
  }
}
